import { ComposerPrimitive, useAuiState } from "@assistant-ui/react";
import { ArrowUp, Square } from "lucide-react";
import type { RefObject } from "react";
import { useContext, useEffect, useState } from "react";
import { CopilotActionContext } from "@/features/copilot/components/copilot-action-context";
import { CopilotBlockingNoticeLine } from "@/features/copilot/components/copilot-blocking-notice";
import { CopilotPageChip } from "@/features/copilot/components/copilot-page-chip";
import { CopilotRuntimeContext } from "@/features/copilot/components/copilot-runtime-context";
import { CopilotStarters } from "@/features/copilot/components/copilot-starters";
import type { CopilotBlockingNotice } from "@/features/copilot/lib/copilot-action-blocking";
import {
  isAwaitingStoppedCopilotTurn,
  isCopilotSendDisabled,
  preventUnavailableCopilotSubmit,
} from "@/features/copilot/lib/copilot-send-policy";
import { useCopilotLayout } from "@/shared/components/layout/copilot-layout.context";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Textarea } from "@/shared/components/ui/textarea";

export function CopilotComposer({
  noCredits,
  serverIsRunning,
  serverTurnStatus,
  blockingNotice,
  composerRef,
}: {
  noCredits: boolean;
  serverIsRunning: boolean;
  serverTurnStatus: string | null | undefined;
  blockingNotice: CopilotBlockingNotice;
  composerRef: RefObject<HTMLTextAreaElement | null>;
}) {
  const actions = useContext(CopilotRuntimeContext);
  const actionState = useContext(CopilotActionContext);
  const layout = useCopilotLayout();
  const runtimeIsRunning = useAuiState((state) => state.thread.isRunning);
  const running = runtimeIsRunning || serverIsRunning;
  const awaitingStoppedTurn = isAwaitingStoppedCopilotTurn(running, serverTurnStatus);
  const sendDisabled = isCopilotSendDisabled({
    noCredits,
    hasPendingActions: blockingNotice !== null,
    approvalSending: actionState?.approvalSending === true,
    awaitingStoppedTurn,
  });
  const [stopError, setStopError] = useState(false);

  useEffect(() => {
    // A touch device would raise the keyboard over the conversation the moment the panel opens.
    if (!layout.isOpen || !window.matchMedia("(pointer: fine)").matches) return;
    window.requestAnimationFrame(() => composerRef.current?.focus());
  }, [composerRef, layout.isOpen]);

  function stop() {
    if (!actions) return;
    setStopError(false);
    void actions
      .stop()
      .then(() => setStopError(false))
      .catch(() => setStopError(true));
  }

  return (
    <div className="border-t border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto w-full max-w-3xl">
        {noCredits ? (
          <p className="mb-2 text-xs text-text-secondary">
            Your AI credit pool is empty. Check your plan or try again after it renews.
          </p>
        ) : null}
        {awaitingStoppedTurn ? (
          <p role="status" className="mb-2 text-xs text-text-secondary">
            Finishing the stopped answer. You can send in a moment.
          </p>
        ) : null}
        {stopError ? (
          <p role="alert" className="mb-2 text-xs text-destructive">
            The stop request did not reach the server. Try stopping again.
          </p>
        ) : null}
        <CopilotBlockingNoticeLine notice={blockingNotice} />
        <CopilotStarters composerRef={composerRef} />
        <ComposerPrimitive.Root
          className="rounded-xl"
          onSubmit={(event) => preventUnavailableCopilotSubmit(event, sendDisabled)}
        >
          <Card className="gap-2 p-2 shadow-xs transition-[border-color,box-shadow] duration-150 focus-within:border-ring/60 focus-within:ring-[3px] focus-within:ring-ring/20">
            <ComposerPrimitive.Input
              id="copilot-composer"
              ref={composerRef}
              aria-label="Message Hand"
              placeholder={
                blockingNotice?.kind === "decide"
                  ? "Decide on the change above first"
                  : "Ask Hand about this workspace"
              }
              submitMode="enter"
              cancelOnEscape={false}
              className="min-h-14 max-h-40 resize-none border-0 bg-transparent px-2 py-1.5 text-base shadow-none dark:bg-transparent md:text-sm focus-visible:ring-0"
              render={<Textarea />}
            />
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1">
                <CopilotPageChip />
              </div>
              {running || stopError ? (
                <Button
                  type="button"
                  size="icon-sm"
                  variant="outline"
                  aria-label="Stop"
                  className="pointer-coarse:size-11"
                  onClick={stop}
                >
                  <Square aria-hidden="true" className="size-3" />
                </Button>
              ) : (
                <ComposerPrimitive.Send asChild disabled={sendDisabled}>
                  <Button
                    type="button"
                    size="icon-sm"
                    className="pointer-coarse:size-11"
                    aria-label="Send message"
                    disabled={sendDisabled}
                  >
                    <ArrowUp aria-hidden="true" />
                  </Button>
                </ComposerPrimitive.Send>
              )}
            </div>
          </Card>
        </ComposerPrimitive.Root>
      </div>
    </div>
  );
}
